import axios, { AxiosInstance } from 'axios'
import {
  Article,
  EmailAddress,
  CreateArticleRequest,
  UpdateArticleRequest,
  ListArticlesRequest,
  ListArticlesResponse,
  SuggestionsResponse,
  ApiResponse,
} from '../types'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Every method below unwraps `ApiResponse` and reads `error.message` off it
    // — but only on a 2xx. On a 4xx/5xx axios rejects first, so that unwrap
    // never runs and the carefully-worded server message is discarded in favour
    // of "Request failed with status code 429". The UI renders `err.message`
    // verbatim, so what the user saw for a rate limit was the status code.
    //
    // The original error object is re-thrown with `message` REPLACED rather
    // than wrapped in a new Error, because callers depend on what it is as much
    // as on what it says: `runSearch` checks `signal.aborted` and axios's own
    // cancellation carries no `response`, so a cancelled request keeps its
    // identity and falls through this untouched.
    this.client.interceptors.response.use(undefined, (error) => {
      const serverMessage = error?.response?.data?.error?.message
      if (typeof serverMessage === 'string' && serverMessage) {
        error.message = serverMessage
      }
      return Promise.reject(error)
    })
  }

  setAuthToken(token: string) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }

  async createArticle(data: CreateArticleRequest): Promise<Article> {
    const response = await this.client.post<ApiResponse<Article>>('/articles', data)
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create article')
    }
    return response.data.data
  }

  async getArticle(id: string): Promise<Article> {
    const response = await this.client.get<ApiResponse<Article>>(`/articles/${id}`)
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get article')
    }
    return response.data.data
  }

  /**
   * List or search articles.
   *
   * Passing `q` switches the endpoint to search mode: a single relevance-ranked
   * page with no `nextToken`, optionally carrying `corrections`. `signal` exists
   * for that path — two fast Enters race, and the loser's response must be
   * discarded rather than rendered over the winner's.
   */
  async listArticles(
    params: ListArticlesRequest,
    signal?: AbortSignal
  ): Promise<ListArticlesResponse> {
    const response = await this.client.get<ApiResponse<ListArticlesResponse>>('/articles', {
      params,
      signal,
    })
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to list articles')
    }
    return response.data.data
  }

  async updateArticle(id: string, data: UpdateArticleRequest): Promise<Article> {
    const response = await this.client.patch<ApiResponse<Article>>(`/articles/${id}`, data)
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update article')
    }
    return response.data.data
  }

  async deleteArticle(id: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(`/articles/${id}`, {
      validateStatus: (s) => s === 200 || s === 404,
    })
    if (response.status === 404) return // already gone — treat as success
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete article')
    }
  }

  async getEmailAddresses(): Promise<EmailAddress[]> {
    const response = await this.client.get<ApiResponse<EmailAddress[]>>('/email-addresses')
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get email addresses')
    }
    return response.data.data
  }

  async addEmailAddress(email: string): Promise<EmailAddress> {
    const response = await this.client.post<ApiResponse<EmailAddress>>('/email-addresses', {
      email,
    })
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to add email address')
    }
    return response.data.data
  }

  async deleteEmailAddress(email: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>(
      `/email-addresses/${encodeURIComponent(email)}`
    )
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete email address')
    }
  }

  async resendVerification(email: string): Promise<void> {
    const response = await this.client.post<ApiResponse>(
      `/email-addresses/${encodeURIComponent(email)}/resend-verification`
    )
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to resend verification')
    }
  }

  async createShareToken(articleId: string): Promise<{ shareUrl: string; expiresAt: string }> {
    const response = await this.client.post<ApiResponse<{ shareUrl: string; expiresAt: string }>>(
      `/articles/${articleId}/share`
    )
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create share link')
    }
    return response.data.data
  }

  async getSharedArticle(
    token: string
  ): Promise<{ title: string; content: string | null; expiresAt: string }> {
    const response = await this.client.get<
      ApiResponse<{ title: string; content: string | null; expiresAt: string }>
    >(`/share/${token}`, { headers: { Authorization: undefined } })
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Link expired')
    }
    return response.data.data
  }

  async verifyEmail(token: string): Promise<{ email: string }> {
    const response = await this.client.get<ApiResponse<{ email: string }>>(
      `/email-addresses/verify`,
      { params: { token } }
    )
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Verification failed')
    }
    return response.data.data
  }

  // The Discover feed recomputes server-side at most once per day; there is no
  // client-forced refresh. This just re-reads the current (possibly background-
  // refreshed) cache.
  async getSuggestions(): Promise<SuggestionsResponse> {
    const response = await this.client.get<ApiResponse<SuggestionsResponse>>('/suggestions')
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to load suggestions')
    }
    return response.data.data
  }

  async dismissSuggestion(url: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>('/suggestions', { data: { url } })
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to dismiss suggestion')
    }
  }

  // Records a save from the Discover feed. Hides the row like a dismiss, but the
  // 'saved' reason routes it to savedUrls server-side so it does NOT pollute the
  // dismissedUrls negative-quality signal.
  async markSuggestionSaved(url: string): Promise<void> {
    const response = await this.client.delete<ApiResponse>('/suggestions', {
      data: { url, reason: 'saved' },
    })
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to mark suggestion saved')
    }
  }
}

export const apiClient = new ApiClient()
